import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';
import { formatDateShort, isSubscriptionActive } from '../../utils/dateTimeHelpers';
import '../../styles/AdminStyles.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    password: '',
    start_date: '',
    end_date: '',
    training_type: 'מכשיר',
    training_days: 12,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userAPI.getAllUsers(10000, 0);
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('שגיאה בטעינת משתמשים');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        full_name: user.full_name,
        phone: user.phone,
        password: '',
        start_date: user.start_date,
        end_date: user.end_date,
        training_type: user.training_type,
        training_days: user.training_days,
      });
    } else {
      setEditingUser(null);
      setFormData({
        full_name: '',
        phone: '',
        password: '',
        start_date: '',
        end_date: '',
        training_type: 'מכשיר',
        training_days: 12,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      full_name: '',
      phone: '',
      password: '',
      start_date: '',
      end_date: '',
      training_type: 'מכשיר',
      training_days: 12,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Check for duplicate name or phone
      const duplicateName = users.find(
        (user) =>
          user.full_name.toLowerCase() === formData.full_name.toLowerCase() &&
          (!editingUser || user.id !== editingUser.id)
      );

      const duplicatePhone = users.find(
        (user) =>
          user.phone === formData.phone &&
          (!editingUser || user.id !== editingUser.id)
      );

      if (duplicateName) {
        alert(`שגיאה: משתמש עם השם "${formData.full_name}" כבר קיים במערכת`);
        return;
      }

      if (duplicatePhone) {
        alert(`שגיאה: משתמש עם מספר הטלפון "${formData.phone}" כבר קיים במערכת`);
        return;
      }

      if (editingUser) {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if empty
        }
        await userAPI.updateUser(editingUser.id, updateData);
        alert('המשתמש עודכן בהצלחה');
      } else {
        // Create new user
        if (!formData.password) {
          alert('נדרשת סיסמה ליצירת משתמש חדש');
          return;
        }
        await userAPI.createUser(formData);
        alert('המשתמש נוצר בהצלחה');
      }

      handleCloseModal();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.error || 'שגיאה בשמירת המשתמש');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את ${userName}?`)) {
      return;
    }

    try {
      await userAPI.deleteUser(userId);
      alert('המשתמש נמחק בהצלחה');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('שגיאה במחיקת המשתמש');
    }
  };

  const filteredUsers = users
    .filter(
      (user) =>
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone.includes(searchTerm)
    )
    .sort((a, b) => {
      const now = new Date();
      const aEndDate = new Date(a.end_date);
      const bEndDate = new Date(b.end_date);
      const aIsActive = aEndDate >= now;
      const bIsActive = bEndDate >= now;

      // Active users always come first
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      // If both are active or both are inactive, sort by end_date (most recent first)
      return bEndDate - aEndDate;
    });

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען משתמשים...</p>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-header">
        <div>
          <h1>ניהול משתמשים</h1>
          <p>סך הכל {users.length} משתמשים</p>
        </div>
        <button className="admin-button-primary" onClick={() => handleOpenModal()}>
          ➕ הוסף משתמש חדש
        </button>
      </div>

      {/* Search */}
      <div className="admin-search-bar">
        <input
          type="text"
          placeholder="🔍 חפש משתמש (שם או טלפון)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-search-input"
        />
      </div>

      {/* Users Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>שם</th>
              <th>טלפון</th>
              <th>סוג אימון</th>
              <th>תאריך התחלה</th>
              <th>תאריך סיום</th>
              <th>מס׳ אימונים</th>
              <th>סטטוס</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.full_name}</td>
                <td>{user.phone}</td>
                <td>
                  <span className="admin-badge">{user.training_type}</span>
                </td>
                <td>{formatDateShort(user.start_date)}</td>
                <td>{formatDateShort(user.end_date)}</td>
                <td>{user.training_days}</td>
                <td>
                  {isSubscriptionActive(user.end_date) ? (
                    <span className="admin-status-badge admin-status-active">
                      פעיל
                    </span>
                  ) : (
                    <span className="admin-status-badge admin-status-expired">
                      פג תוקף
                    </span>
                  )}
                </td>
                <td>
                  <div className="admin-actions">
                    <button
                      className="admin-button-edit"
                      onClick={() => handleOpenModal(user)}
                      title="ערוך"
                    >
                      ✏️
                    </button>
                    <button
                      className="admin-button-delete"
                      onClick={() => handleDeleteUser(user.id, user.full_name)}
                      title="מחק"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="admin-empty-state">
            <p>לא נמצאו משתמשים</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingUser ? 'ערוך משתמש' : 'משתמש חדש'}</h2>
              <button className="admin-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>שם מלא *</label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="admin-form-group">
                  <label>טלפון *</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="05xxxxxxxx"
                    required
                  />
                </div>
              </div>

              <div className="admin-form-group">
                <label>
                  סיסמה {editingUser ? '(השאר ריק לשמור את הקיימת)' : '*'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>תאריך התחלה *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="admin-form-group">
                  <label>תאריך סיום *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>סוג אימון *</label>
                  <select
                    name="training_type"
                    value={formData.training_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="בר">בר</option>
                    <option value="מכשיר">מכשיר</option>
                    <option value="מזרן">מזרן</option>
                    <option value="בהריון">בהריון</option>
                  </select>
                </div>

                <div className="admin-form-group">
                  <label>מספר אימונים *</label>
                  <input
                    type="number"
                    name="training_days"
                    value={formData.training_days}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-button-secondary"
                  onClick={handleCloseModal}
                >
                  ביטול
                </button>
                <button type="submit" className="admin-button-primary">
                  {editingUser ? 'עדכן' : 'צור'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
